/**
 * taskDates.ts — visible date metadata helpers for the task board.
 *
 * BOARD-DATE-VISIBILITY-1: makes task age visible in cards/timelines without
 * pretending every task is scheduled.
 *
 * Semantics (Director ruling):
 *   - `due_date` is the only field that means "scheduled / planned work".
 *   - `created_at` / `updated_at` are metadata. They MUST NOT be promoted into
 *     the scheduled-date timeline as if they were due dates.
 *   - The card's visible-date chip prefers `due_date`; falls back to
 *     `updated_at`, then `created_at`. The label changes to make the
 *     distinction obvious to the reader.
 */
import type { TaskItem } from '@/api/types';

export type TaskDateKind = 'due' | 'updated' | 'created';

export interface TaskCardDate {
  /** Which underlying task field this came from. */
  kind: TaskDateKind;
  /** The raw ISO string from the task record (passthrough, not modified). */
  iso: string;
  /** Human-friendly date — e.g. "Apr 3" or "Jan 4 '25" depending on age. */
  formatted: string;
  /** Compact label for the chip — e.g. "Due Apr 3" / "Updated Apr 3". */
  label: string;
  /** True when `kind === 'due'` and the date is in the past for an active row. */
  isOverdue: boolean;
}

const DAY_MS = 86400000;

/**
 * Pick the most informative date for a Kanban card.
 *
 *   - prefer `due_date` (scheduled work); else
 *   - `updated_at` (recent activity signal); else
 *   - `created_at` (filing date — every task has this).
 *
 * Returns null only if none parse, which should never happen in practice
 * because `created_at` is required by the backend schema.
 */
export function pickCardDate(task: TaskItem, now: Date = new Date()): TaskCardDate | null {
  const candidates: Array<{ kind: TaskDateKind; iso: string | null | undefined }> = [
    { kind: 'due', iso: task.due_date },
    { kind: 'updated', iso: task.updated_at },
    { kind: 'created', iso: task.created_at },
  ];

  for (const c of candidates) {
    if (!c.iso) continue;
    const d = new Date(c.iso);
    if (Number.isNaN(d.getTime())) continue;
    const formatted = formatTaskDate(c.iso, now);
    const label = labelFor(c.kind, formatted);
    const isOverdue =
      c.kind === 'due' &&
      task.status !== 'done' &&
      task.status !== 'cancelled' &&
      d.getTime() < now.getTime();
    return { kind: c.kind, iso: c.iso, formatted, label, isOverdue };
  }
  return null;
}

/**
 * Format an ISO date into a compact, scannable string.
 *
 *   - within 7 days (UTC calendar day delta): "Today" / "Yesterday"
 *     / "Nd ago" / "in Nd" / "Tomorrow"
 *   - same year: "Apr 3"
 *   - older: "Apr 3 '25"
 *
 * Uses UTC calendar-day buckets so the relative tag is stable regardless
 * of the runner's local timezone — important because the source records
 * are stored in UTC by the engine.
 */
export function formatTaskDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const utcDayOf = (x: Date) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
  // Negative for past, positive for future (signed UTC-day delta).
  const dayDelta = Math.round((utcDayOf(d) - utcDayOf(now)) / DAY_MS);

  if (dayDelta === 0) return 'Today';
  if (dayDelta === 1) return 'Tomorrow';
  if (dayDelta === -1) return 'Yesterday';
  if (dayDelta > 0 && dayDelta <= 7) return `in ${dayDelta}d`;
  if (dayDelta < 0 && dayDelta >= -7) return `${Math.abs(dayDelta)}d ago`;

  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  const monthDay = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  if (sameYear) return monthDay;
  // Two-digit year suffix to keep chip narrow ("Apr 3 '25").
  const yy = String(d.getUTCFullYear()).slice(-2);
  return `${monthDay} '${yy}`;
}

/** Build the chip label that prefixes the formatted date. */
export function labelFor(kind: TaskDateKind, formatted: string): string {
  switch (kind) {
    case 'due':
      return `Due ${formatted}`;
    case 'updated':
      return `Updated ${formatted}`;
    case 'created':
      return `Created ${formatted}`;
  }
}

/**
 * Count "undated active tasks" — rows that have no `due_date` but ARE active
 * work. Used by the Timeline view to surface that those tasks still exist.
 *
 * "Active" = not `done` and not `cancelled`.
 */
export function countUndatedActive(tasks: TaskItem[]): number {
  let n = 0;
  for (const t of tasks) {
    if (t.due_date) continue;
    if (t.status === 'done' || t.status === 'cancelled') continue;
    n += 1;
  }
  return n;
}

/**
 * Produce the explanatory empty-state text for the Timeline's
 * dated-tasks-in-next-60-days section, including a count of undated active
 * work so the section never reads as "no tasks" when there are plenty of them.
 *
 * Wording is scoped to the next-60-days filter the Timeline view applies, so a
 * board with far-future-dated tasks (>60 days out) doesn't get a falsely
 * absolute "no due dates yet" message (review M1, 2026-04-28).
 */
export function timelineDatedEmptyMessage(undatedActiveCount: number): string {
  const base =
    'No tasks have due dates in the next 60 days. Created/updated dates are still visible on cards and in task details.';
  if (undatedActiveCount > 0) {
    return `${base} ${undatedActiveCount} active task${undatedActiveCount === 1 ? '' : 's'} ${undatedActiveCount === 1 ? 'is' : 'are'} unscheduled.`;
  }
  return base;
}
